import {
  useQuery,
  useInfiniteQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  UserKeys,
  getAllSharedDriveFolders,
  getAllSharedDrivesForDomainQuery,
  getPageOfSharedDrivesForDomainV2,
} from '../../../api/users-api';
import {
  isAbleToSeeUIElement,
  DriveRoles,
} from '../../../utils/roles/client-roles';
import SharedDrivesChildGrid from '../../user-management/user-details/drive/shared-drives/shared-drives-child/shared-drives-child-grid';
import SharedDrivesFolderTree from '../../user-management/user-details/drive/shared-drives/shared-drives-folder-tree';
import SharedDrivesGrid from '../../user-management/user-details/drive/shared-drives/shared-drives-parent/shared-drives-grid';
import { Page, SharedDriveModel } from '../../../../types';
import SharedDrivesPermissionCheckDialog from '../../user-management/user-details/drive/shared-drives/shared-drives-parent/shared-drives-permission-check-dialog';
import SharedErrorBoundary from '../../../shared/shared-error-boundary';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import SharedDrivesPaginatedGrid from '../../user-management/user-details/drive/shared-drives/shared-drives-parent/shared-drives-paginated-grid';
import { useForm } from 'react-hook-form';
import SharedDrivesGridFilters from '../../user-management/user-details/drive/shared-drives/shared-drives-parent/shared-drives-paginated-grid-filters';
import SharedDrivesIndexedGrid from './shared-drives-indexed-grid';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
interface SharedDrivesAdminGridWithTreeProps {
  primaryDomain: string;
  loggedInUser: string;
  driveRoles: string[];
  changeLogRoles: string[];
  canFetch?: boolean;
  confirmedChangeLogMessage?: string;
  showChangeLogOverlay?: boolean;
  onOpenChangeLogDialog?: () => void;
}

export interface SharedDrivesSearchForm {
  name: string;
  driveId: string;
}

export default function SharedDrivesAdminGridWithTree({
  primaryDomain,
  loggedInUser,
  driveRoles,
  changeLogRoles,
  canFetch,
  confirmedChangeLogMessage,
  showChangeLogOverlay = false,
  onOpenChangeLogDialog,
}: SharedDrivesAdminGridWithTreeProps) {
  const { features, viewType, currentTier } = useUserSettings();
  const queryClient = useQueryClient();
  const [selectedFolder, setSelectedFolder] = useState('shared-drives');
  const [openPermissionCheckDialog, setOpenPermissionCheckDialog] =
    useState(false);
  const [permissionCheckLoading, setPermissionCheckLoading] = useState(false);

  const [selectedDrive, setSelectedDrive] = useState<SharedDriveModel | null>(
    null
  );

  const canSeeSharedDrives = isAbleToSeeUIElement(
    driveRoles,
    DriveRoles.ViewSharedDrives.value
  );

  const shouldPrefetchSharedDrive = isFeatureEnabled(
    'prefetchSharedDrives',
    features
  );

  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
    cursor: string | null;
    search: string;
    driveId: string;
  }>({
    pageSize: 50,
    page: 0,
    cursor: null,
    search: '',
    driveId: '',
  });

  const [paginationSortModel, setPaginationSortModel] = useState<{
    column: string;
    sortDirection: 'ASC' | 'DESC';
  }>({
    column: 'name',
    sortDirection: 'ASC',
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
    watch,
    setValue,
  } = useForm<SharedDrivesSearchForm>({
    defaultValues: {
      name: paginationModel.search,
      driveId: paginationModel.driveId,
    },
  });

  const {
    data: sharedDrivesPage,
    isLoading: sharedDrivesLoading,
    isFetching: sharedDrivesFetching,
  } = useQuery({
    queryKey: [
      UserKeys.SharedDrivesForDomainPage,
      paginationModel.page,
      paginationModel.pageSize,
      paginationModel.search,
      paginationModel.driveId,
    ],
    queryFn: () => {
      const object: Page<SharedDriveModel[]> = {
        results: [],
        firstPull: paginationModel.page === 0,
        searchTime: 0,
        pageSize: paginationModel.pageSize,
        cursor: paginationModel.cursor,
        nameSpace: '',
      };
      return getPageOfSharedDrivesForDomainV2(
        object,
        paginationModel.search,
        paginationModel.driveId,
        confirmedChangeLogMessage
      );
    },
    enabled:
      canSeeSharedDrives &&
      (canFetch ?? true) &&
      isFeatureEnabled('showPaginatedSharedDrives', features),
    placeholderData: keepPreviousData,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('shared drives'),
    },
  });

  const {
    data: allFolders,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [UserKeys.SharedDriveFolders, loggedInUser],
    queryFn: ({ pageParam }) => {
      return getAllSharedDriveFolders(
        pageParam,
        paginationSortModel.column,
        paginationSortModel.sortDirection
      );
    },
    initialPageParam: selectedFolder,
    getNextPageParam: () => {
      return selectedFolder;
    },
    enabled:
      canSeeSharedDrives &&
      selectedFolder !== 'shared-drives' &&
      currentTier !== 'enterprise' &&
      !isFeatureEnabled('showPaginatedSharedDrives', features),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('folder data'),
    },
  });

  const defaultStaleTime = queryClient
    .getDefaultOptions()
    .queries?.staleTime?.valueOf() as number;

  const {
    data: sharedDrives,
    isLoading,
    isFetching,
  } = useQuery(
    getAllSharedDrivesForDomainQuery({
      enabled:
        canSeeSharedDrives &&
        (canFetch ?? true) &&
        !isFeatureEnabled('showPaginatedSharedDrives', features) &&
        currentTier !== 'enterprise',
      staleTime: shouldPrefetchSharedDrive ? Infinity : defaultStaleTime,
      changeLogMessage: confirmedChangeLogMessage,
    })
  );

  useEffect(() => {
    if (
      selectedFolder !== 'shared-drives' &&
      !isFetchingNextPage &&
      !allFolders?.pageParams.includes(selectedFolder)
    ) {
      fetchNextPage();
    }
  }, [
    allFolders?.pageParams,
    fetchNextPage,
    isFetchingNextPage,
    selectedFolder,
  ]);

  return (
    <Stack
      flexDirection="row"
      justifyContent="space-between"
      sx={{ minHeight: 420, height: '100%' }}
      gap={2}
    >
      {currentTier === 'enterprise' ? (
        <>
          <SharedErrorBoundary section="Shared Drives Grid">
            <SharedDrivesIndexedGrid
              key={
                viewType.sharedDrivesIndexedGridStates?.activeGridState ||
                'default'
              }
              userEmail={loggedInUser}
              primaryDomain={primaryDomain}
              driveRoles={driveRoles}
              changeLogRoles={changeLogRoles}
              setSelectedFolder={setSelectedFolder}
              selectedDrive={selectedDrive}
              setSelectedDrive={setSelectedDrive}
              setOpenPermissionCheckDialog={setOpenPermissionCheckDialog}
              permissionCheckLoading={permissionCheckLoading}
              setPermissionCheckLoading={setPermissionCheckLoading}
              selectedFolder={selectedFolder}
              canFetch={canFetch}
              confirmedChangeLogMessage={confirmedChangeLogMessage}
              showChangeLogOverlay={showChangeLogOverlay}
              onOpenChangeLogDialog={onOpenChangeLogDialog}
            />
          </SharedErrorBoundary>
        </>
      ) : !isFeatureEnabled('showPaginatedSharedDrives', features) ? (
        <>
          <Card sx={{ width: '18vw' }}>
            <SharedErrorBoundary section="Shared Drives Folder Tree">
              <SharedDrivesFolderTree
                userEmail={loggedInUser}
                selectedFolder={selectedFolder}
                setSelectedFolder={setSelectedFolder}
                allFolders={allFolders}
                isLoading={false}
                sharedDrives={sharedDrives || []}
                setOpenPermissionCheckDialog={setOpenPermissionCheckDialog}
                setPermissionCheckLoading={setPermissionCheckLoading}
                setSelectedDrive={setSelectedDrive}
                permissionCheckLoading={permissionCheckLoading}
              />
            </SharedErrorBoundary>
          </Card>
          {selectedFolder === 'shared-drives' ? (
            <SharedErrorBoundary section="Shared Drives Grid">
              <SharedDrivesGrid
                key={
                  viewType.sharedDrivesGridStates?.activeGridState || 'default'
                }
                userEmail={loggedInUser}
                isLoading={isLoading || isFetching}
                sharedDrives={sharedDrives}
                primaryDomain={primaryDomain}
                driveRoles={driveRoles}
                changeLogRoles={changeLogRoles}
                setSelectedFolder={setSelectedFolder}
                selectedDrive={selectedDrive}
                setSelectedDrive={setSelectedDrive}
                setOpenPermissionCheckDialog={setOpenPermissionCheckDialog}
                permissionCheckLoading={permissionCheckLoading}
                setPermissionCheckLoading={setPermissionCheckLoading}
                showChangeLogOverlay={showChangeLogOverlay}
                onOpenChangeLogDialog={onOpenChangeLogDialog}
              />
            </SharedErrorBoundary>
          ) : (
            <SharedErrorBoundary section="Shared Drives Child Grid">
              <SharedDrivesChildGrid
                userEmail={loggedInUser}
                primaryDomain={primaryDomain}
                driveRoles={driveRoles}
                changeLogRoles={changeLogRoles}
                isLoading={isLoading || isFetching}
                selectedFolder={selectedFolder}
                setSelectedFolder={setSelectedFolder}
                sharedDrives={sharedDrives || []}
                paginationModel={paginationSortModel}
                setPaginationModel={setPaginationSortModel}
              />
            </SharedErrorBoundary>
          )}
        </>
      ) : (
        <>
          {selectedFolder === 'shared-drives' ? (
            <Box
              sx={{
                minHeight: 600,
                height: '72vh',
                width: '95%',
                mx: 'auto',
                mt: 1,
              }}
            >
              <Stack sx={{ height: '100%' }} gap={2} flexDirection={'row'}>
                <SharedDrivesGridFilters
                  control={control}
                  isLoading={sharedDrivesLoading || sharedDrivesFetching}
                  handleSubmit={handleSubmit}
                  isDirty={isDirty}
                  reset={reset}
                  paginationModel={paginationModel}
                  setPaginationModel={setPaginationModel}
                  watch={watch}
                  setValue={setValue}
                />
                <SharedErrorBoundary section="Shared Drives Grid">
                  <SharedDrivesPaginatedGrid
                    userEmail={loggedInUser}
                    isLoading={sharedDrivesLoading || sharedDrivesFetching}
                    sharedDrives={sharedDrivesPage}
                    primaryDomain={primaryDomain}
                    driveRoles={driveRoles}
                    changeLogRoles={changeLogRoles}
                    setSelectedFolder={setSelectedFolder}
                    selectedDrive={selectedDrive}
                    setSelectedDrive={setSelectedDrive}
                    setOpenPermissionCheckDialog={setOpenPermissionCheckDialog}
                    permissionCheckLoading={permissionCheckLoading}
                    setPermissionCheckLoading={setPermissionCheckLoading}
                    paginationModel={paginationModel}
                    setPaginationModel={setPaginationModel}
                    showChangeLogOverlay={showChangeLogOverlay}
                    onOpenChangeLogDialog={onOpenChangeLogDialog}
                  />
                </SharedErrorBoundary>
              </Stack>
            </Box>
          ) : (
            <SharedErrorBoundary section="Shared Drives Child Grid">
              <SharedDrivesChildGrid
                userEmail={loggedInUser}
                primaryDomain={primaryDomain}
                driveRoles={driveRoles}
                changeLogRoles={changeLogRoles}
                isLoading={isLoading || isFetching}
                selectedFolder={selectedFolder}
                setSelectedFolder={setSelectedFolder}
                sharedDrives={sharedDrivesPage?.results || []}
                paginationModel={paginationSortModel}
                setPaginationModel={setPaginationSortModel}
              />
            </SharedErrorBoundary>
          )}
        </>
      )}
      {selectedDrive && openPermissionCheckDialog ? (
        <SharedErrorBoundary section="Shared Drives Permission Check Dialog">
          <SharedDrivesPermissionCheckDialog
            open={openPermissionCheckDialog}
            handleClose={() => {
              setOpenPermissionCheckDialog(false);
              setSelectedDrive(null);
            }}
            minimumRequiredRole="reader"
            folderOrDriveId={selectedDrive.id}
            actionAfterPermissionGranted={() => {
              setSelectedFolder(selectedDrive.id);
              setSelectedDrive(null);
            }}
            nameOfSharedDrive={selectedDrive.name}
          />
        </SharedErrorBoundary>
      ) : null}
    </Stack>
  );
}
